import { env } from "config";
import { useAuth } from "hooks/useAuth";

/**
 * Converts an Azurite blob storage URL to use the Vite proxy in development.
 * In development, replaces http://localhost:10000 with /azurite to use the proxy.
 * In production, returns the URL as-is.
 * @param {string} url - The blob storage URL
 * @returns {string} - The converted URL
 */
const convertBlobUrl = (url) => {
  if (!url) return url;

  const isDevelopment = import.meta.env.VITE_REACT_APP_ENVIRONMENT === "Local";
  const azuriteLocalUrl = "http://localhost:10000";
  const proxyPath = "/azurite";

  if (isDevelopment && url.includes(azuriteLocalUrl)) {
    return url.replace(azuriteLocalUrl, proxyPath);
  }

  return url;
};

async function downloadFromUrl(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to download file: ${res.status}`);
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "";
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error("downloadFromUrl error:", error);
  }
}


const createAuthenticatedFetch = (ensureValidToken) => {
  return async (url, options = {}) => {
    try {
      const validToken = await ensureValidToken();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${validToken}`,
        },
      });

      return response;
    } catch (error) {
      if (error.message && error.message.includes("Authentication required")) {
        throw error;
      }

      console.error("Authenticated fetch failed:", error);
      throw error;
    }
  };
};

export const horusApi = () => {
  const { ensureValidToken } = useAuth();
  const authenticatedFetch = createAuthenticatedFetch(ensureValidToken);

  return {
    /* DATASET */
    getDatasets: async ({
      onlyReady = false,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/dataset?onlyReady=${onlyReady}&includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    downloadInputFiles: async ({ id, datasetType }) => {
      try {
        const normalizedDatasetType = Number(datasetType);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/dataset/downloadinputs/${id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify([normalizedDatasetType]),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw {
            status: response.status,
            ...errorData,
          };
        }

        const links = await response.json();

        if (Array.isArray(links.fileLinks)) {
          links.fileLinks.forEach((file) => {
            const proxiedUrl = convertBlobUrl(file.url);
            downloadFromUrl(proxiedUrl, file.fileName);
          });
        }

        return links;
      } catch (error) {
        console.error(
          "Error during computation input files download:",
          error
        );
        throw error;
      }
    },

    updateDataset: async (data) => {
      try {
        const formData = new FormData();
        formData.append("Description", data.description);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/dataset/${data.id}`,
          {
            method: "PATCH",
            body: formData,
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    addDataset: async (data) => {
      const formData = new FormData();
      formData.append("description", data.description);

      if (data.staticData) formData.append("staticData", data.staticData);
      if (data.ead) formData.append("cashflows", data.ead);

      const response = await authenticatedFetch(
        `${env.gatewayUrl}/horus/dataset`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw {
          status: response.status,
          ...errorData,
        };
      }

      return await response.json();
    },

    deleteDataset: async (id) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/dataset/${id}`,
          {
            method: "DELETE",
          }
        );
        return response.ok;
      } catch (error) {
        throw error;
      }
    },

    /* COMPUTATION */
    getComputations: async ({
      onlyReady = false,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation?onlyReady=${onlyReady}&includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getComputation: async ({
      id,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation/${id}?includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.text();
      } catch (error) {
        throw error;
      }
    },

    addComputation: async (data) => {
      try {
        const formData = new FormData();

        formData.append("Description", data.description || "");
        formData.append("DatasetId", data.datasetId);
        formData.append("CouponMethod", data.couponMethod);
        formData.append("ActuarialRateRules", data.actuarialRateRules);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw {
            status: response.status,
            ...errorData,
          };
        }

        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    updateComputation: async (data) => {
      try {
        const formData = new FormData();
        formData.append("DatasetId", data.datasetId);
        formData.append("Description", data.description);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation/${data.id}`,
          {
            method: "PATCH",
            body: formData,
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    deleteComputation: async (id) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation/${id}`,
          {
            method: "DELETE",
          }
        );
        return response.ok;
      } catch (error) {
        throw error;
      }
    },

    /* VARIATION ANALYSIS */
    getVariationAnalysis: async ({
      id,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis/${id}?includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.text();
      } catch (error) {
        throw error;
      }
    },

    getVariations: async ({
      onlyReady = false,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis?onlyReady=${onlyReady}&includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    deleteVariationAnalysis: async (id) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis/${id}`,
          {
            method: "DELETE",
          }
        );
        return response.ok;
      } catch (error) {
        throw error;
      }
    },

    addVariationAnalysis: async (data) => {
      try {
        const formData = new FormData();
        formData.append("Description", data.description);
        formData.append("DatasetDepartureId", data.datasetDepartureId);
        formData.append("DatasetArrivalId", data.datasetArrivalId);
        formData.append("CouponMethod", data.couponMethod);
        formData.append(
          "ActuarialRateRulesDeparture",
          data.actuarialRateRulesDeparture ?? 0
        );
        formData.append(
          "ActuarialRateRulesArrival",
          data.actuarialRateRulesArrival ?? 0
        );

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw {
            status: response.status,
            message:
              errorData?.message || errorData?.error || "An error occurred",
            ...errorData,
          };
        }

        return await response.json();
      } catch (error) {
        console.error("Error when adding a variation analysis:", error);
        throw error;
      }
    },

    updateVariationAnalysis: async (data) => {
      try {
        const formData = new FormData();

        formData.append("Description", data.description);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis/${data.id}`,
          {
            method: "PATCH",
            body: formData,
          }
        );
        return await response.json();
      } catch (error) {
        console.error("Error when updating a variation analysis:", error);
        throw error;
      }
    },

    /* CHARTS */
    getDashboardCharts: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/dashboard`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getComputationCharts: async (computationId) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation/charts/${computationId}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.json();
      } catch (error) {
        console.error("Error during computation charts retrieval:", error);
        throw error;
      }
    },

    getControlCharts: async (computationId) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/horus/control/charts/${computationId}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.json();
      } catch (error) {
        console.error("Error during control charts retrieval:", error);
        throw error;
      }
    },

    getVariationCharts: async (variationAnalysisId) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis/charts/${variationAnalysisId}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.json();
      } catch (error) {
        console.error("Error during variation charts retrieval:", error);
        throw error;
      }
    },

    /* RESULT */
    getResultHeaderComputation: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/result-header/simple/2`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getResultHeaderControl: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/result-header/simple/3`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getComputationResult: async (id) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/horus/computation/result/${id}`,
          { method: "GET" }
        );

        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.text();
      } catch (error) {
        console.error("Error during computation result retrieval:", error);
        throw error;
      }
    },

    getControlResult: async (id) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/horus/control/result/${id}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.text();
      } catch (error) {
        console.error("Error during control result retrieval:", error);
        throw error;
      }
    },

    getSelectedVariationAnalysis: async (id) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/horus/variation-analysis/result/${id}`,
          { method: "GET" }
        );

        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.text();
      } catch (error) {
        console.error(
          "Error during variation analysis result retrieval:",
          error
        );
        throw error;
      }
    },

    /* Analysis */
    getPivotHeaderComputation: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/pivot-header/simple/2`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getPivotHeaderControl: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/pivot-header/simple/3`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getPivotHeaderVariationAnalysis: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/horus/pivot-header/simple/4`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },
  };
};
